/**
 * Informational / capability view for CLI-based connectors.
 *
 * Rendered on the connector detail page in place of the per-tool action and
 * per-trigger card lists when a toolset is CLI-backed (`integration.cli_name`).
 * It explains, in plain language, what the connector lets the agent do and
 * shows example prompts a user can ask. Curated copy comes from
 * `cliConnectorContent`, with a generic fallback for any uncurated CLI toolset.
 *
 * Product Mode, Editorial register: mono section-numbers and a single restrained
 * plum accent, capabilities laid out as a hairline grid, example prompts framed
 * with a full hairline border + bg-tint + leading glyph (never a side-stripe).
 */

import type { IntegrationEnhanced } from "@/api/integrations";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { getCliConnectorContent } from "./cliConnectorContent";

interface CliConnectorOverviewProps {
  integration: IntegrationEnhanced;
  connected: boolean;
}

const SectionHead: React.FC<{ num: string; title: string }> = ({
  num,
  title,
}) => (
  <div className="flex items-baseline gap-2">
    <span className="font-mono uppercase tracking-[0.08em] text-n-50 text-[length:var(--text-2xs)]">
      {num}
    </span>
    <h3 className="text-sm font-semibold tracking-tight text-foreground">
      {title}
    </h3>
  </div>
);

export const CliConnectorOverview: React.FC<CliConnectorOverviewProps> = ({
  integration,
  connected,
}) => {
  const content = getCliConnectorContent(integration);

  // The shared header already renders integration.description; only show the
  // curated tagline when it adds something the header doesn't.
  const showTagline = content.tagline !== integration.description;

  return (
    <div className="space-y-8">
      {showTagline && (
        <p className="max-w-[68ch] text-base leading-relaxed text-muted-foreground">
          {content.tagline}
        </p>
      )}

      {/* Capabilities — hairline grid, one accent glyph per cell */}
      <section className="space-y-4">
        <SectionHead num="01" title="What you can do" />
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-1 gap-px bg-border @md:grid-cols-3">
            {content.capabilities.map((capability) => {
              const Icon = capability.icon;
              return (
                <div
                  key={capability.title}
                  className="flex flex-col gap-3 bg-card p-5"
                >
                  <Icon className="h-5 w-5 text-plum" strokeWidth={1.75} />
                  <div className="space-y-1">
                    <div className="text-sm font-semibold text-foreground">
                      {capability.title}
                    </div>
                    <div className="text-sm leading-relaxed text-muted-foreground">
                      {capability.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Example prompts — full hairline border + bg-tint + leading glyph */}
      <section className="space-y-4">
        <SectionHead num="02" title="Try asking" />
        <ul className="space-y-2">
          {content.examples.map((example) => (
            <li
              key={example}
              className="flex items-start gap-3 rounded-md border border-border bg-n-05 px-4 py-3 text-sm leading-relaxed text-foreground"
            >
              <MessageCircle
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-plum"
                strokeWidth={1.75}
              />
              <span>{example}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">
          {connected
            ? `Ask in any chat — the agent reaches for ${integration.name} when it helps.`
            : `Connect ${integration.name} to start using these with the agent.`}
        </p>
      </section>

      {/* Technical details — how credentials are used */}
      <section className="space-y-4">
        <SectionHead num="03" title="How your connection is used" />
        <div className="flex items-start gap-3 rounded-md border border-border bg-n-05 px-4 py-3">
          <ShieldCheck
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal"
            strokeWidth={1.75}
          />
          <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <p>
              When you connect {integration.name}, your authorization is used by
              a code-execution container at runtime. Inside it, your agent runs
              the
              {integration.cli_name ? (
                <>
                  {" "}
                  <code className="rounded bg-n-10 px-1 py-0.5 font-mono text-xs text-foreground">
                    {integration.cli_name}
                  </code>{" "}
                  command-line tool
                </>
              ) : (
                " connector's command-line tool"
              )}{" "}
              to carry out what you ask, scoped to the access you granted.
            </p>
            <p>
              Operations run with your credentials, so activity is attributable
              to your account. Disconnect at any time to immediately revoke
              access.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
